import { useState } from 'react';
import { makeDifficultyExplanation } from '../../services/conceptPlus1Service';
import MarkdownExplanation from '../MarkdownExplanation';

type Level = 'basic' | 'standard' | 'advanced';

interface Props {
  conceptName: string;
  gradeLevel: number;
  studentId: string;
  explanationBasic?: string | null;
  explanationStandard?: string | null;
  explanationAdvanced?: string | null;
}

const LEVELS: { key: Level; label: string; colour: string }[] = [
  { key: 'basic',    label: 'Basic',    colour: 'bg-green-100 text-green-800 border-green-300' },
  { key: 'standard', label: 'Standard', colour: 'bg-blue-100 text-blue-800 border-blue-300' },
  { key: 'advanced', label: 'Advanced', colour: 'bg-purple-100 text-purple-800 border-purple-300' },
];

export default function DifficultySlider({ conceptName, gradeLevel, studentId, explanationBasic, explanationStandard, explanationAdvanced }: Props) {
  const [level, setLevel] = useState<Level>('standard');
  const [loading, setLoading] = useState(false);
  const [aiText, setAiText] = useState<Partial<Record<Level, string>>>({});

  const dbText: Record<Level, string | null | undefined> = {
    basic:    explanationBasic,
    standard: explanationStandard,
    advanced: explanationAdvanced,
  };

  const getText = (l: Level) => aiText[l] ?? dbText[l] ?? null;

  const handleSelect = async (l: Level) => {
    setLevel(l);
    if (getText(l) || l === 'standard') return;
    setLoading(true);
    try {
      const res = await makeDifficultyExplanation({ studentId, conceptName, gradeLevel, level: l as 'basic' | 'advanced' });
      if (res.answer) setAiText(prev => ({ ...prev, [l]: res.answer }));
    } finally {
      setLoading(false);
    }
  };

  const text = getText(level);

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {LEVELS.map(({ key, label, colour }) => (
          <button
            key={key}
            onClick={() => handleSelect(key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
              level === key ? colour : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="min-h-[80px]">
        {loading ? (
          <div className="space-y-2 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
          </div>
        ) : text ? (
          <MarkdownExplanation
            text={text}
            variant="light"
            className="text-gray-800 text-sm sm:text-base leading-relaxed"
          />
        ) : (
          <p className="text-gray-400 text-sm italic">Select a level to load explanation.</p>
        )}
      </div>
    </div>
  );
}
